import React, { useRef, useState, useEffect } from 'react';

export type Parasha = { english: string; hebrew: string };

interface ParashaSelectorProps {
  parashot: Parasha[];
  selected: Parasha | null;
  onSelect: (p: Parasha) => void;
  label: string;
  noParashaYetText: string;
  chooseAnotherHint: string;
}

const ParashaSelector: React.FC<ParashaSelectorProps> = ({
  parashot,
  selected,
  onSelect,
  label,
  noParashaYetText,
  chooseAnotherHint,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const displayParasha = selected;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ color: '#7B8799', fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={chooseAnotherHint}
        style={{
          width: '100%',
          textAlign: 'right',
          fontSize: 22,
          fontWeight: 800,
          color: '#0F264A',
          marginBottom: 6,
          padding: '8px 12px',
          borderRadius: 12,
          border: '2px solid #E8ECF3',
          background: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        <span style={{ flex: 1 }} dir="rtl">
          {displayParasha?.hebrew ?? noParashaYetText}
        </span>
        <span
          style={{
            fontSize: 14,
            color: '#7B8799',
            fontWeight: 600,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
          aria-hidden
        >
          ▼
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={chooseAnotherHint}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 4,
            maxHeight: 260,
            overflowY: 'auto',
            background: 'white',
            borderRadius: 12,
            border: '1px solid #E8ECF3',
            boxShadow: '0 6px 22px rgba(15, 38, 74, 0.12)',
            zIndex: 10,
            padding: '6px 0',
            direction: 'rtl',
          }}
        >
          {parashot.map((p) => {
            const isActive = selected?.hebrew === p.hebrew && selected?.english === p.english;
            return (
              <button
                key={`${p.english}-${p.hebrew}`}
                role="option"
                aria-selected={isActive}
                type="button"
                onClick={() => {
                  onSelect(p);
                  setOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: isActive ? '#132A49' : 'transparent',
                  color: isActive ? '#F8E5A9' : '#0F264A',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: 18,
                  cursor: 'pointer',
                  textAlign: 'right',
                  transition: 'all 0.2s ease',
                }}
              >
                {p.hebrew}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ParashaSelector;
